import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@zentera/ui';
import { fmt } from './format.js';
import { startRecording, voiceSupported, type RecordedAudio, type VoiceRecorder } from './voice.js';
import { WhatsAppText } from './whatsapp-text.js';

/** O que o cliente mandou: texto ou mensagem de voz (com a transcrição, quando já feita). */
export type ChatPart = { kind: 'text'; text: string; at: number } | { kind: 'audio'; audio: RecordedAudio; at: number; transcript?: string };

export interface ChatTurn<E = unknown> {
  id: number;
  customer: ChatPart[];
  reply: string | null;
  repliedAt: number;
  /** Setor que respondeu. */
  profileName?: string | null;
  /** Tempo que o assistente levou (ms), mostrado discretamente sob a resposta. */
  responseMs?: number;
  extra: E;
}

/** Corpo enviado ao servidor para um lote de mensagens. */
export type PartPayload = { text: string } | { audio: { data: string; mime_type: string } };

export interface ChatResult<E> {
  reply: string | null;
  profileName?: string | null;
  responseMs?: number;
  /** Transcrição de cada áudio do lote, na ordem. */
  transcripts?: string[];
  extra: E;
}

/**
 * Conversa de teste que se comporta como o atendimento real: as mensagens aparecem na hora e,
 * depois de `windowSeconds` sem mensagem nova, vão juntas para o assistente ("mensagens picadas").
 */
export function useTestChat<E>({ windowSeconds, post }: { windowSeconds: number; post: (parts: PartPayload[]) => Promise<ChatResult<E>> }) {
  const [turns, setTurns] = useState<ChatTurn<E>[]>([]);
  const [queue, setQueue] = useState<ChatPart[]>([]);
  const [inflight, setInflight] = useState<ChatPart[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queueRef = useRef<ChatPart[]>([]);
  const busy = useRef(false);
  const failed = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const postRef = useRef(post);
  postRef.current = post;
  const windowRef = useRef(windowSeconds);
  windowRef.current = windowSeconds;

  const flush = useCallback(async () => {
    if (busy.current || !queueRef.current.length) return;
    busy.current = true;
    failed.current = false;
    const batch = queueRef.current;
    queueRef.current = [];
    setQueue([]);
    setInflight(batch);
    setError(null);
    const started = Date.now();
    try {
      const r = await postRef.current(batch.map((p) => (p.kind === 'text' ? { text: p.text } : { audio: { data: p.audio.base64, mime_type: p.audio.mimeType } })));
      // Um instante de "digitando…" mesmo quando a resposta é imediata (modo simulação).
      const elapsed = Date.now() - started;
      if (elapsed < 700) await new Promise((res) => setTimeout(res, 700 - elapsed));
      let a = 0;
      const customer = batch.map((p) => (p.kind === 'audio' ? { ...p, transcript: r.transcripts?.[a++] } : p));
      setTurns((t) => [...t, { id: Date.now(), customer, reply: r.reply, repliedAt: Date.now(), profileName: r.profileName, responseMs: r.responseMs, extra: r.extra }]);
    } catch (e) {
      failed.current = true;
      setError(e instanceof Error ? e.message : 'Falha ao enviar');
      // Mensagens não respondidas voltam para a fila e vão junto com a próxima.
      queueRef.current = [...batch, ...queueRef.current];
      setQueue(queueRef.current);
    } finally {
      setInflight(null);
      busy.current = false;
      if (queueRef.current.length && !failed.current) schedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), Math.max(0, windowRef.current) * 1000);
  }, [flush]);

  const push = useCallback(
    (part: ChatPart) => {
      queueRef.current = [...queueRef.current, part];
      setQueue(queueRef.current);
      // Cada mensagem nova reinicia a espera; enquanto o assistente responde, a próxima leva aguarda.
      if (!busy.current) schedule();
    },
    [schedule],
  );

  const reset = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    queueRef.current = [];
    setQueue([]);
    setTurns([]);
    setError(null);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return { turns, queue, inflight, error, push, reset };
}

const time = (ms: number) => new Date(ms).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
const duration = (ms: number) => {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Três pontinhos animados, como no WhatsApp. */
function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 py-1" aria-hidden>
      {[0, 150, 300].map((d) => (
        <span key={d} className="size-1.5 animate-bounce rounded-full bg-fg-3" style={{ animationDelay: `${d}ms`, animationDuration: '1s' }} />
      ))}
    </span>
  );
}

function Checks({ read }: { read: boolean }) {
  // ✓✓ azul quando o assistente já leu (respondeu ou está respondendo); cinza enquanto aguarda.
  return (
    <svg viewBox="0 0 16 11" className={cn('h-2.5 w-3.5 shrink-0', read ? 'text-[#34b7f1]' : 'text-fg-4')} aria-label={read ? 'Lida' : 'Entregue'}>
      <path d="M11.07.65 4.5 7.22 1.93 4.65.86 5.72 4.5 9.36l7.64-7.64z M15.07.65 8.5 7.22l-.94-.94-1.07 1.07 2.01 2.01 7.64-7.64z" fill="currentColor" />
    </svg>
  );
}

const MicIcon = () => (
  <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden>
    <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-2.08A7 7 0 0 0 19 12Z" />
  </svg>
);

function CustomerBubble({ part, read }: { part: ChatPart; read: boolean }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[78%] rounded-lg rounded-tr-sm bg-[#d9fdd3] px-3 py-1.5 text-sm text-[#111b21] shadow-xs">
        {part.kind === 'text' ? (
          <WhatsAppText text={part.text} />
        ) : (
          <span className="block">
            <span className="flex items-center gap-2 text-[#3b4a54]">
              <MicIcon />
              Mensagem de voz · {duration(part.audio.durationMs)}
            </span>
            {part.transcript !== undefined && <span className="mt-1 block text-xs text-[#54656f] italic">“{part.transcript || 'sem fala reconhecível'}”</span>}
          </span>
        )}
        <span className="float-right mt-1.5 ml-2 flex items-center gap-1 text-2xs text-[#667781]">
          {time(part.at)} <Checks read={read} />
        </span>
      </div>
    </div>
  );
}

/**
 * Conversa de teste com cara de WhatsApp: as mensagens do cliente aparecem na hora, o assistente
 * "digita" enquanto o motor responde, e cada resposta pode ser selecionada para ver o rastro.
 */
export function ChatThread<E>({
  chat,
  selectedId,
  onSelect,
  title,
  subtitle,
  disabled,
  placeholder = 'Mensagem do cliente',
  emptyHint = 'Envie uma mensagem como se fosse o cliente.',
  windowSeconds,
}: {
  chat: ReturnType<typeof useTestChat<E>>;
  selectedId?: number | null;
  onSelect?: (id: number) => void;
  title: string;
  /** Linha sob o nome (setor atual, identificação…); trocada por "digitando…" durante a resposta. */
  subtitle?: ReactNode;
  disabled?: boolean;
  placeholder?: string;
  emptyHint?: string;
  windowSeconds: number;
}) {
  const { turns, queue, inflight, error, push } = chat;
  const [text, setText] = useState('');
  const [recording, setRecording] = useState<{ rec: VoiceRecorder; started: number } | null>(null);
  const recordingRef = useRef(recording);
  recordingRef.current = recording;
  const [recError, setRecError] = useState<string | null>(null);
  const [, tick] = useState(0);
  const end = useRef<HTMLLIElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns.length, queue.length, inflight]);
  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, [recording]);
  // Saindo da tela no meio de uma gravação: libera o microfone.
  useEffect(() => () => recordingRef.current?.rec.cancel(), []);

  const submit = () => {
    const t = text.trim();
    if (!t || disabled) return;
    push({ kind: 'text', text: t, at: Date.now() });
    setText('');
    input.current?.focus();
  };

  const stopRecording = async () => {
    const current = recordingRef.current;
    if (!current) return;
    setRecording(null);
    try {
      const audio = await current.rec.stop();
      if (audio.durationMs < 600) return; // toque acidental
      push({ kind: 'audio', audio, at: Date.now() });
    } catch (e) {
      setRecError(e instanceof Error ? e.message : 'Não foi possível gravar');
    }
  };
  const toggleMic = async () => {
    setRecError(null);
    if (recordingRef.current) return stopRecording();
    try {
      const rec = await startRecording(() => void stopRecording());
      setRecording({ rec, started: Date.now() });
    } catch {
      setRecError('Permita o uso do microfone no navegador para gravar.');
    }
  };

  const typing = !!inflight;

  return (
    <div className="flex min-h-[480px] flex-col overflow-hidden rounded-lg border border-line">
      <div className="flex items-center gap-3 border-b border-line bg-bg px-4 py-2.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand text-sm font-medium text-white" aria-hidden>
          {title.slice(0, 1).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{title}</div>
          <div className="truncate text-xs text-fg-3" aria-live="polite">
            {typing ? <span className="text-success">digitando…</span> : subtitle}
          </div>
        </div>
      </div>

      <ol className="flex-1 space-y-1.5 overflow-y-auto bg-subtle px-3 py-4 sm:px-5" style={{ maxHeight: 560 }}>
        {turns.length === 0 && !queue.length && !inflight && <li className="py-12 text-center text-sm text-fg-4">{emptyHint}</li>}
        {turns.map((t) => (
          <li key={t.id} className="space-y-1.5">
            {t.customer.map((p, i) => (
              <CustomerBubble key={i} part={p} read />
            ))}
            {t.reply ? (
              <div className="flex justify-start">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect?.(t.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect?.(t.id);
                    }
                  }}
                  className={cn(
                    'max-w-[82%] cursor-pointer rounded-lg rounded-tl-sm bg-bg px-3 py-1.5 text-left text-sm shadow-xs ring-1 transition-shadow outline-none focus-visible:ring-brand',
                    selectedId === t.id ? 'ring-brand' : 'ring-transparent hover:ring-line',
                  )}
                  title="Ver o rastro desta resposta"
                >
                  {t.profileName && <div className="mb-0.5 text-xs font-medium text-brand">{t.profileName}</div>}
                  <WhatsAppText text={t.reply} />
                  <span className="float-right mt-1.5 ml-2 text-2xs text-fg-4">
                    {t.responseMs != null && <span className="mr-1.5">{fmt.ms(t.responseMs)}</span>}
                    {time(t.repliedAt)}
                  </span>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => onSelect?.(t.id)} className="block w-full py-1 text-center text-xs text-fg-4 hover:text-fg-2">
                Conversa transferida para atendimento humano · ver rastro
              </button>
            )}
          </li>
        ))}
        {inflight && (
          <li className="space-y-1.5">
            {inflight.map((p, i) => (
              <CustomerBubble key={i} part={p} read />
            ))}
            <div className="flex justify-start" role="status" aria-label="Assistente digitando">
              <div className="rounded-lg rounded-tl-sm bg-bg px-3 py-1.5 shadow-xs">
                <TypingDots />
              </div>
            </div>
          </li>
        )}
        {queue.length > 0 && (
          <li className="space-y-1.5">
            {queue.map((p, i) => (
              <CustomerBubble key={i} part={p} read={false} />
            ))}
            {!typing && windowSeconds > 0 && !error && (
              <p className="text-right text-2xs text-fg-4">aguardando {windowSeconds} s sem mensagem nova para responder tudo junto…</p>
            )}
          </li>
        )}
        <li ref={end} aria-hidden />
      </ol>

      {(error || recError) && (
        <p role="alert" className="border-t border-line bg-danger-subtle px-4 py-2 text-sm text-danger">
          {error ?? recError}
          {error && queue.length > 0 && <span className="text-fg-3"> — as mensagens não respondidas vão junto com a próxima.</span>}
        </p>
      )}
      <form
        className="flex items-end gap-2 border-t border-line bg-bg p-2.5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {recording ? (
          <div className="flex min-h-9 flex-1 items-center gap-2 rounded-full border border-danger/40 bg-danger-subtle px-4 text-sm text-danger" role="status">
            <span className="size-2 animate-pulse rounded-full bg-danger" aria-hidden />
            Gravando · {duration(Date.now() - recording.started)} — toque no microfone para enviar
          </div>
        ) : (
          <textarea
            ref={input}
            aria-label="Mensagem do cliente"
            rows={1}
            value={text}
            disabled={disabled}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              // Enter envia; Shift+Enter quebra linha (como no WhatsApp Web).
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={placeholder}
            className="max-h-32 min-h-9 flex-1 resize-none rounded-full border border-line bg-subtle px-4 py-2 text-sm outline-none focus:border-brand-line focus:ring-2 focus:ring-brand/15 disabled:opacity-60"
            style={{ fieldSizing: 'content' } as React.CSSProperties}
          />
        )}
        {voiceSupported() && (
          <button
            type="button"
            onClick={() => void toggleMic()}
            disabled={disabled}
            aria-label={recording ? 'Parar e enviar áudio' : 'Gravar áudio'}
            className={cn(
              'flex size-9 shrink-0 items-center justify-center rounded-full transition-colors disabled:opacity-40',
              recording ? 'bg-danger text-white' : 'bg-subtle text-fg-2 hover:bg-muted',
            )}
          >
            <MicIcon />
          </button>
        )}
        {!recording && (
          <button
            type="submit"
            disabled={!text.trim() || disabled}
            aria-label="Enviar"
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand text-white transition-opacity disabled:opacity-40"
          >
            <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden>
              <path d="M3.4 20.4 21 12 3.4 3.6 3.4 10.1 15 12 3.4 13.9z" />
            </svg>
          </button>
        )}
      </form>
    </div>
  );
}
